import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PermissionCode } from '../user';
import { getPermission } from './query/usePermissionQuery';

export function useHasPermissionForDescription(): (
  defaultPermissionCode: PermissionCode,
  defaultDescriptionIdOrCode: number | string
) => boolean | undefined;
export function useHasPermissionForDescription(
  defaultPermissionCode: PermissionCode,
  defaultDescriptionIdOrCode: number | string
): boolean | undefined;
export function useHasPermissionForDescription(
  defaultPermissionCode?: PermissionCode,
  defaultDescriptionIdOrCode?: number | string
) {
  const [data, setData] = useState<boolean | undefined>(undefined);
  const paramsRef = useRef<{
    permissionCode?: PermissionCode;
    descriptionIdOrCode?: number | string;
  }>({});

  const get = useCallback(
    (permissionCode: PermissionCode, descriptionIdOrCode: number | string) => {
      if (
        paramsRef.current.permissionCode === permissionCode &&
        paramsRef.current.descriptionIdOrCode === descriptionIdOrCode
      ) {
        return;
      }

      paramsRef.current = { permissionCode, descriptionIdOrCode };

      getPermission({
        permissionCode,
        ...(typeof descriptionIdOrCode === 'number'
          ? { descriptionId: descriptionIdOrCode }
          : { descriptionCode: descriptionIdOrCode }),
      }).then(setData);
    },
    []
  );

  useEffect(() => {
    if (
      defaultPermissionCode !== undefined &&
      defaultDescriptionIdOrCode !== undefined
    ) {
      get(defaultPermissionCode, defaultDescriptionIdOrCode);
    }
  }, [defaultPermissionCode, defaultDescriptionIdOrCode, get]);

  return useMemo(
    () =>
      defaultPermissionCode !== undefined &&
      defaultDescriptionIdOrCode !== undefined
        ? data
        : (
            permissionCode: PermissionCode,
            descriptionIdOrCode: number | string
          ) => {
            get(permissionCode, descriptionIdOrCode);
            return data;
          },
    [defaultPermissionCode, defaultDescriptionIdOrCode, get, data]
  );
}
